/*#
# landform

**Authored landforms as terrain overrides** — where [[slope-profile]]s *remap*
the noise (levels curves), a landform *forces* a shape through it: a volcano
cone, an impact crater, wherever you say. Pure, deterministic, unit-tested;
[[b3d-terrain]] applies its `landform` hook after the profile pipeline in
origin-stable coordinates, so authored shapes survive floating-origin resets
and light correctly (normals difference the hooked field).

The factories return a **matched pair** — the height `landform` and the
`provinceField` that makes it glow — because a volcano isn't a shape OR a
material, it's both:

```javascript
const vesuvius = volcano({ x: 45, z: -25, radius: 55, height: 24, baseLevel: 5 })
terrain.landform = vesuvius.landform
terrain.provinceField = vesuvius.province
terrain.regenerate()
```

> Shown, not run: it needs a `terrain` from the surrounding scene. Working
> volcanoes are in [biome-chart](/biome-chart/)'s demo (a glowing cone at the
> shore) and on the [[sdf-lattice]] page, where `volcano` builds the cone you
> can cut open.

And a runtime explosion is the same move with the other factory — stamp an
`impactCrater` at the hit point, compose it in, `regenerate()`: a glowing
crater with almost no effort. `pad` is the same idea for CIVILIZATION — a
dead-flat surface with a cut-and-fill skirt, which is how cities and bases
claim ground (terrace several up a hillside with `composeLandforms`).
`composeLandforms` chains shapes; `mergeProvinces` maxes glow fields.

## A field knows where it stops

Every factory here returns a field tagged with an **`extent`** — the world-space
AABB outside which it is inert. A province has a natural boundary; it just
spans more than one tile, so the question a tile has to ask is
rectangle-vs-rectangle:

```javascript
const vesuvius = volcano({ x: 45, z: -25, radius: 55, height: 24 })
extentOf(vesuvius.province)                     // {minX, minZ, maxX, maxZ}
touchesExtent(vesuvius.province, x0, z0, x1, z1) // does it reach this tile?
```

A bare `(x, z) => number` cannot be asked that, and without it terrain would
sample every province at every vertex of every tile in the world. `b3d-terrain`
skips a province that cannot reach the tile it is building, which is exactly
equivalent (the field returns `0` out there), and the same test serves any
per-tile decision.

**The landform and the province carry SEPARATE extents**, because they genuinely
differ: a volcano's glow tail dies at `craterRadius + radius * 0.4` — inside the
cone for a default crater and *outside* it for a wide caldera — while a crater's
glow stops at `0.85 R` and its rim runs to `1.25 R`. One box for both is wrong
in one direction or the other every time.

⚠️ **An extent is a promise, and a wrong one is invisible.** Callers may skip the
field entirely outside it, so an extent that is too SMALL clips the thing it
describes with no error; too large only costs work. A hand-written closure that
declares none is treated as unbounded, and one unbounded member makes a whole `mergeProvinces`/`composeLandforms`
unbounded, because a box around only the members that declared one is a promise
nobody made.
*/
/*{ "parent": "b3d-terrain", "order": 40 }*/

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v)
const smooth = (t: number) => {
  const c = clamp01(t)
  return c * c * (3 - 2 * c)
}

/**
 * WHERE A FIELD STOPS — the rectangle outside which it is guaranteed inert.
 *
 * Every factory here already knows its own footprint: `volcano({radius})`
 * early-returns `h` past the radius, `gulley` bails outside its corridor. But
 * that knowledge was closed over and thrown away, so the only thing terrain
 * ever received was a bare `(x, z) => number` — and a point sampler cannot
 * answer "does this province touch this tile", which is the question any
 * per-tile decision has to ask first. Tonio: "A province has a natural
 * boundary, right? It just can affect more than one tile."
 *
 * Both halves of that are why this is an AABB in world space rather than a
 * circle: a province spans tiles, so the useful query is rectangle-vs-rectangle
 * against a tile, and an axis-aligned box is the one shape every footprint —
 * disc, oriented corridor, traced polygon — can be reduced to without knowing
 * what it was.
 *
 * ⚠️ AN EXTENT IS A PROMISE, AND A WRONG ONE IS INVISIBLE. Callers are entitled
 * to skip the field entirely outside it, so an extent that is too SMALL silently
 * clips the thing it describes — geometry that simply is not there, with no
 * error. Too large only costs work. When in doubt, round outward.
 */
export interface Extent {
  minX: number
  minZ: number
  maxX: number
  maxZ: number
}

/** A field function that knows where it stops. */
export type Bounded<F> = F & { extent: Extent }

export type LandformFn = (x: number, z: number, h: number) => number
export type ProvinceFn = (x: number, z: number) => number

/** Tag a field with the rectangle outside which it does nothing. */
export function withExtent<F extends (...args: never[]) => number>(
  fn: F,
  extent: Extent
): Bounded<F> {
  return Object.assign(fn, { extent })
}

/** The AABB of a disc — the footprint shape most of these factories have. */
export const circleExtent = (x: number, z: number, r: number): Extent => ({
  minX: x - r,
  minZ: z - r,
  maxX: x + r,
  maxZ: z + r,
})

/**
 * The AABB of an ORIENTED corridor — `gulley` and `cover` work in (along,
 * lateral) about a heading, so their world footprint is a rotated rectangle and
 * its bounding box is not simply the corridor's own dimensions.
 */
export function corridorExtent(
  x: number,
  z: number,
  headingDeg: number,
  alongMin: number,
  alongMax: number,
  halfLateral: number
): Extent {
  const a = headingDeg * (Math.PI / 180)
  const cos = Math.cos(a)
  const sin = Math.sin(a)
  const ax0 = alongMin * cos
  const ax1 = alongMax * cos
  const az0 = alongMin * sin
  const az1 = alongMax * sin
  const padX = halfLateral * Math.abs(sin)
  const padZ = halfLateral * Math.abs(cos)
  return {
    minX: x + Math.min(ax0, ax1) - padX,
    maxX: x + Math.max(ax0, ax1) + padX,
    minZ: z + Math.min(az0, az1) - padZ,
    maxZ: z + Math.max(az0, az1) + padZ,
  }
}

/**
 * The extent a field declares, or `null` for a hand-written closure that
 * declares none. `null` means UNBOUNDED — "I might do something anywhere" — so
 * an unannotated field keeps working exactly as before. Fail-open is the only
 * safe default here: guessing a boundary for a function that never promised one
 * would clip it.
 */
export function extentOf(fn: unknown): Extent | null {
  const e = (fn as { extent?: Extent } | null | undefined)?.extent
  return e != null &&
    typeof e.minX === 'number' &&
    typeof e.maxX === 'number' &&
    typeof e.minZ === 'number' &&
    typeof e.maxZ === 'number'
    ? e
    : null
}

/** The smallest box containing both. */
export const unionExtent = (a: Extent, b: Extent): Extent => ({
  minX: Math.min(a.minX, b.minX),
  minZ: Math.min(a.minZ, b.minZ),
  maxX: Math.max(a.maxX, b.maxX),
  maxZ: Math.max(a.maxZ, b.maxZ),
})

/**
 * Could this field do anything inside this rectangle? An unbounded field (no
 * extent) always answers yes, so this is safe to put in front of any sampler.
 *
 * Inclusive on the edges: a field whose footprint exactly abuts a tile still
 * counts, because the shared boundary vertices belong to both.
 */
export function touchesExtent(
  fn: unknown,
  minX: number,
  minZ: number,
  maxX: number,
  maxZ: number
): boolean {
  const e = extentOf(fn)
  if (e == null) return true
  return e.minX <= maxX && e.maxX >= minX && e.minZ <= maxZ && e.maxZ >= minZ
}

/** A landform + its matching volcanism province, made together. */
/**
 * A place where a landform is ACTIVE (EFFECTS-DESIGN, PROVINCE-DESIGN): an
 * effect preset at a point, sized and as busy as the landform says. A
 * terrain given these (`terrain.vents`) puts an effect on each, on the ground.
 */
export interface Vent {
  x: number
  z: number
  /** An effect preset: `volcano`, `geyser`, `bubbles`, … */
  preset: string
  /** The effect's `scale` (its presets are person-sized). */
  scale: number
  /** The effect's activity, 0 to 1. */
  activity: number
}

export interface AuthoredLandform {
  /** Where it is active: effects the terrain places (a volcano's vent). */
  vents?: Vent[]
  landform: Bounded<LandformFn>
  /**
   * The province carries its OWN extent, deliberately not shared with the
   * landform's — the two genuinely differ. A `volcano`'s glow tail dies at
   * `craterRadius + radius * 0.4`, which is well inside the cone for a default
   * crater and OUTSIDE it for a wide one; a crater's glow stops at `0.85 R`
   * while its rim runs to `1.25 R`. One box for both would be wrong in one
   * direction or the other every time.
   */
  province: Bounded<ProvinceFn>
}

export interface VolcanoOptions {
  /**
   * How active, 0 to 1: the vent's `volcano` effect (0.15 dormant, 0.5
   * semi-active, 1 erupting). 0, the default, is extinct: no effect.
   */
  activity?: number
  /** Vent position (world coords). */
  x: number
  z: number
  /** Footprint radius (m) — outside it the terrain is untouched. */
  radius: number
  /** Edifice height (m) above `baseLevel`. */
  height: number
  /** The terrain level the cone rises from — the flanks BLEND the noise
   * toward this before adding the cone, so the edifice dominates its local
   * noise instead of riding it. Default 0 (sea level). */
  baseLevel?: number
  /** Caldera radius (m). Default `radius * 0.22`. */
  craterRadius?: number
  /** Caldera depth (m) below the rim — the lava pool sits at rim − depth,
   * so a smaller value holds the melt higher in the bowl. Default
   * `height * 0.35`. */
  craterDepth?: number
  /** How much the flanks suppress the underlying noise (0..1). Default 0.7. */
  flatten?: number
  /** Province intensity at the vent (0..1 → the volcanism ladder). The glow
   * fades down the flanks and ends before the footprint. Default 1. */
  glow?: number
}

/**
 * A classic volcano that fades in as an override: smoothstep-blended flanks
 * (C1 at the footprint edge — no seam against the noise terrain), a steepened
 * cone, a caldera sunk below the rim, and a matching province — molten on the
 * caldera floor, faint seams on the upper flanks, bare basalt below them, the
 * living biome beyond.
 */
/** Province value of a volcano's bare-basalt flank: past the shader's 0.12
 * (fully rock) and under its 0.14 (first seams). */
const BASALT_HEAT = 0.13
/** Province value at the crater rim: faint seams. The wall climbs from here. */
const RIM_HEAT = 0.25

export function volcano(opts: VolcanoOptions): AuthoredLandform {
  const {
    x: cx,
    z: cz,
    radius,
    height,
    baseLevel = 0,
    craterRadius = radius * 0.22,
    craterDepth = height * 0.35,
    flatten = 0.7,
    glow = 1,
    activity = 0,
  } = opts
  // The vent's effect is sized by the crater: the volcano preset is a vent
  // about 4.5 m across, so a 55 m crater takes it at 12.
  const vents: Vent[] =
    activity > 0
      ? [
          {
            x: cx,
            z: cz,
            preset: 'volcano',
            scale: craterRadius / 4.5,
            activity: Math.min(1, activity),
          },
        ]
      : []
  const landform = (x: number, z: number, h: number) => {
    const dx = x - cx
    const dz = z - cz
    const d = Math.sqrt(dx * dx + dz * dz)
    if (d >= radius) return h
    // The edifice profile holds its RIM value across the crater interior
    // (dc clamp), so the caldera floor is genuinely level — a basin, not a
    // funnel; the pool needs somewhere flat to sit. The crater term then
    // sinks a flat floor (full depth out to ~0.55 crater radii, wall to rim).
    const dc = Math.max(d, craterRadius)
    const dome = smooth(1 - dc / radius)
    const cone = Math.pow(dome, 1.6)
    const crater = smooth((craterRadius - d) / (craterRadius * 0.45))
    const based = h + (baseLevel - h) * dome * clamp01(flatten)
    return based + height * cone - craterDepth * crater
  }
  const province = (x: number, z: number) => {
    const dx = x - cx
    const dz = z - cz
    const d = Math.sqrt(dx * dx + dz * dz)
    // Full intensity (pools) ONLY on the flat caldera floor. The crater wall
    // climbs to it from the rim's value, so stronger veins start INSIDE the
    // crater and nowhere else.
    const floorR = craterRadius * 0.55
    if (d <= floorR) return glow
    if (d <= craterRadius) {
      const wall = smooth(1 - (d - floorR) / (craterRadius - floorR))
      return glow * (RIM_HEAT + (1 - RIM_HEAT) * wall)
    }
    /*
    OUTSIDE, walking in from the edge: biome, then PLAIN BASALT, then faint
    veins, and only then the crater.

    `t` is how far you have come from the province's edge (0) to the rim (1).
    The shader blends the biome to basalt by a value of 0.12 and draws no
    seams on a local province below 0.14, so:

      t 0    to 0.25   the value rises to BASALT_HEAT: the ground turns to rock
      t 0.25 to 0.55   it holds there: bare basalt, no seams at all
      t 0.55 to 1      it rises to RIM_HEAT: seams fade in, faintly

    Earlier versions cooled DOWN from a glowing rim, which put seams on most
    of the flank and squeezed the basalt into the outer edge. Tonio: "It goes
    from surrounding landscape straight to lava veins. I think it should go
    from surrounding landscape to basalt and only subtly transition to veined
    lava later (say 50-60% of the way to the cone) and then not get stronger
    veins until you're inside the cone."
    */
    const t = 1 - clamp01((d - craterRadius) / (radius * 0.75))
    const basalt = smooth(clamp01(t / 0.25))
    const veins = smooth(clamp01((t - 0.55) / 0.45))
    return glow * (BASALT_HEAT * basalt + (RIM_HEAT - BASALT_HEAT) * veins)
  }
  return {
    vents,
    landform: withExtent(landform, circleExtent(cx, cz, radius)),
    // The basalt apron reaches `craterRadius + radius * 0.75`, NOT `radius`.
    // With the default crater (0.22 R) that is 0.97 R — inside the cone — but
    // `craterRadius` is an option, and a wide caldera pushes the apron past
    // the edifice. Derived rather than assumed, so it stays right when
    // someone authors a crater that is most of the mountain.
    province: withExtent(
      province,
      circleExtent(cx, cz, craterRadius + radius * 0.75)
    ),
  }
}

export interface CraterOptions {
  /** Impact point (world coords). */
  x: number
  z: number
  /** Crater radius (m) — the rim crest sits just inside it. */
  radius: number
  /** Bowl depth (m) below the original terrain. */
  depth: number
  /** Raised-rim height (m). Default `depth * 0.3`. */
  rimHeight?: number
  /** Province intensity at the floor (0..1). ~0.5 reads as cooling ember
   * veins, 1 as a molten floor. Default 0.8. */
  glow?: number
}

/**
 * An impact/explosion crater: a bowl sunk into the EXISTING terrain (no
 * flattening — the scar inherits the landscape), a raised rim, and a hot
 * floor whose glow fades by the rim. Compose one in at a detonation point
 * and `regenerate()` — the aftermath is two field functions.
 */
export function impactCrater(opts: CraterOptions): AuthoredLandform {
  const {
    x: cx,
    z: cz,
    radius,
    depth,
    rimHeight = depth * 0.3,
    glow = 0.8,
  } = opts
  const landform = (x: number, z: number, h: number) => {
    const dx = x - cx
    const dz = z - cz
    const d = Math.sqrt(dx * dx + dz * dz)
    if (d >= radius * 1.25) return h
    const bowl = Math.pow(smooth(1 - d / radius), 1.4)
    // rim: a smooth bump straddling the crest, fading to nothing by 1.25R
    const rt = 1 - Math.abs(d - radius * 0.9) / (radius * 0.35)
    const rim = smooth(rt)
    return h - depth * bowl + rimHeight * rim
  }
  const province = (x: number, z: number) => {
    const dx = x - cx
    const dz = z - cz
    const d = Math.sqrt(dx * dx + dz * dz)
    return glow * smooth(1 - d / (radius * 0.85))
  }
  return {
    // The rim fades to nothing by 1.25 R — the bowl is only the inner part.
    landform: withExtent(landform, circleExtent(cx, cz, radius * 1.25)),
    // The floor glow is gone by 0.85 R, well before the rim.
    province: withExtent(province, circleExtent(cx, cz, radius * 0.85)),
  }
}

export interface CraterFieldOptions {
  /** Same seed, same craters. */
  seed?: number
  /** Smallest and largest crater RADIUS (m). Sizes come in doubling classes
   * between them. */
  minRadius?: number
  maxRadius?: number
  /** How cratered, 0-1: the chance a class's cell holds a crater. 0 = none,
   * ~0.3 Mars, ~0.9 the Moon's highlands (saturated: craters on craters). */
  density?: number
  /** Bowl depth as a fraction of the radius. Fresh simple craters are about
   * 0.4 (depth/diameter ~0.2). */
  depthRatio?: number
  /** How worn, 0-1: 0 = every crater fresh, 1 = old ones nearly erased.
   * Each crater gets its own age up to this. */
  wear?: number
  /** Above this radius (m) a crater is COMPLEX: a flat floor and a central
   * peak, as real ones are past a few km. */
  complexRadius?: number
}

/** Integer hash to [0, 1), deterministic everywhere. */
function hash01i(a: number, b: number, c: number, d: number): number {
  let h = Math.imul(a | 0, 0x9e3779b1) ^ Math.imul(b | 0, 0x85ebca77)
  h = Math.imul(h ^ (h >>> 15), 0xc2b2ae3d) ^ Math.imul(c | 0, 0x27d4eb2f)
  h = Math.imul(h ^ (h >>> 13), 0x165667b1) ^ Math.imul(d | 0, 0x61c88647)
  h ^= h >>> 16
  h = Math.imul(h, 0x85ebca77)
  h ^= h >>> 13
  return (h >>> 0) / 4294967296
}

/**
 * **A crater FIELD**: a whole cratered surface, seeded and unbounded, for
 * Mars, the Moon, Mercury (board #2442). Where `impactCrater` is one scar at a
 * point, this is a population.
 *
 * - **Sizes in doubling classes**, and a real population's count goes as
 *   1/size squared. Each class has its own grid, cells sized to its craters,
 *   so the EXPECTED count per cell is the same in every class: one presence
 *   roll per cell does it, and a crater sits wholly inside its own cell. A
 *   sample therefore checks ONE cell per class, a handful of craters per
 *   terrain vertex, not a search.
 * - **Big first, small last.** Big craters are old; small, young ones punch
 *   into them, which is how real cratered ground overlaps.
 * - **Each crater is worn to its own age** (up to `wear`): shallower, softer
 *   rim. And past `complexRadius` it has a flat floor and a central peak.
 *
 * Returns a landform (no province: craters do not glow), unbounded.
 */
export function craterField(opts: CraterFieldOptions = {}): LandformFn {
  const {
    seed = 1,
    minRadius = 12,
    maxRadius = 600,
    density = 0.3,
    depthRatio = 0.4,
    wear = 0.6,
    complexRadius = 250,
  } = opts
  const p = Math.max(0, Math.min(1, density))
  const classes: number[] = []
  for (let r = Math.max(1, minRadius); r <= maxRadius * 1.0001; r *= 2)
    classes.push(r)
  // Biggest (oldest) first.
  classes.reverse()
  // Cell size in crater radii: a crater of radius up to 2r (the class spans
  // r..2r) plus its 1.25 rim must fit inside with room to jitter.
  const CELL = 7
  return (x: number, z: number, h: number) => {
    if (p <= 0) return h
    let acc = h
    for (let k = 0; k < classes.length; k++) {
      const r0 = classes[k]
      const cell = r0 * CELL
      const ix = Math.floor(x / cell)
      const iz = Math.floor(z / cell)
      if (hash01i(seed, ix, iz, k * 7 + 1) >= p) continue
      const radius = r0 * (1 + hash01i(seed, ix, iz, k * 7 + 2))
      const reach = radius * 1.25
      // Jitter the centre within the cell so the whole crater stays inside.
      const span = cell - 2 * reach
      const cx = ix * cell + reach + hash01i(seed, ix, iz, k * 7 + 3) * span
      const cz = iz * cell + reach + hash01i(seed, ix, iz, k * 7 + 4) * span
      const dx = x - cx
      const dz = z - cz
      const d = Math.sqrt(dx * dx + dz * dz)
      if (d >= reach) continue
      const age =
        hash01i(seed, ix, iz, k * 7 + 5) * Math.max(0, Math.min(1, wear))
      acc += craterProfile(d, radius, depthRatio, age, complexRadius)
    }
    return acc
  }
}

/**
 * ONE CRATER's height change at distance `d` from its centre: a bowl, a
 * raised rim, and past `complexRadius` a flat floor and a central peak. `age`
 * (0 fresh … 1 worn) makes it shallower with a softer rim. The flat field and
 * the sphere's share it, so a crater is the same shape on either.
 */
export function craterProfile(
  d: number,
  radius: number,
  depthRatio: number,
  age: number,
  complexRadius: number
): number {
  const depth = radius * depthRatio * (1 - 0.75 * age)
  const rimHeight = depth * 0.3 * (1 - 0.8 * age)
  let bowl = Math.pow(smooth(1 - d / radius), 1.4)
  let acc = 0
  if (radius > complexRadius) {
    // COMPLEX: a flat floor (the bowl saturates) and a central peak.
    bowl = Math.min(1, bowl * 1.6)
    const peak = smooth(1 - d / (radius * 0.18))
    acc += depth * 0.35 * peak
  }
  const rt = 1 - Math.abs(d - radius * 0.9) / (radius * 0.35)
  return acc - depth * bowl + rimHeight * smooth(rt)
}

export interface SphereCraterOptions {
  seed?: number
  /** Smallest and largest crater radius, as fractions of the body's radius. */
  minRadius?: number
  maxRadius?: number
  /** How cratered, 0…1, as `craterField`'s. */
  density?: number
  depthRatio?: number
  wear?: number
  /** Above this radius (fraction of the body's) a crater is complex. */
  complexRadius?: number
  /**
   * WHERE THE CRATERS FALL MORE: a unit direction in the body's frame, and
   * how much more, -1…1. A tidally locked moon is struck more on its leading
   * side (facing its motion); one sheltered by its planet is struck less on
   * the side toward it. 0 is even.
   */
  facing?: { x: number; y: number; z: number }
  asymmetry?: number
}

/**
 * **Craters on a sphere.** `craterField` on a whole body: give it a point on
 * the unit sphere and it returns the height change there, as a fraction of
 * the body's radius.
 *
 * The cells are cubes in 3D, each crater's centre jittered inside its cube
 * and pushed onto the sphere, and distance is measured between points on it,
 * so there is no seam and no squeeze at the poles. A crater's centre can land
 * up to half a cell outside its cube, so a sample checks its own cell and the
 * 26 around it, per size class.
 */
export function sphereCraters(
  opts: SphereCraterOptions = {}
): (x: number, y: number, z: number) => number {
  const {
    seed = 1,
    minRadius = 0.01,
    maxRadius = 0.2,
    density = 0.5,
    depthRatio = 0.4,
    wear = 0.6,
    complexRadius = 0.06,
    facing = { x: 0, y: 0, z: 1 },
    asymmetry = 0,
  } = opts
  const p = Math.max(0, Math.min(1, density))
  const classes: number[] = []
  for (let r = Math.max(1e-4, minRadius); r <= maxRadius * 1.0001; r *= 2)
    classes.push(r)
  classes.reverse()
  const CELL = 7
  const fl = Math.hypot(facing.x, facing.y, facing.z) || 1
  const fx = facing.x / fl
  const fy = facing.y / fl
  const fz = facing.z / fl
  return (x: number, y: number, z: number) => {
    if (p <= 0) return 0
    let acc = 0
    for (let k = 0; k < classes.length; k++) {
      const r0 = classes[k]
      const cell = r0 * CELL
      const cx0 = Math.floor(x / cell)
      const cy0 = Math.floor(y / cell)
      const cz0 = Math.floor(z / cell)
      for (let ix = cx0 - 1; ix <= cx0 + 1; ix++)
        for (let iy = cy0 - 1; iy <= cy0 + 1; iy++)
          for (let iz = cz0 - 1; iz <= cz0 + 1; iz++) {
            const key = Math.imul(iy, 73856093) ^ Math.imul(iz, 19349663)
            const radius = r0 * (1 + hash01i(seed, ix, key, k * 7 + 2))
            const reach = radius * 1.25
            const span = Math.max(0, cell - 2 * reach)
            const jx =
              ix * cell + reach + hash01i(seed, ix, key, k * 7 + 3) * span
            const jy =
              iy * cell + reach + hash01i(seed, ix, key, k * 7 + 4) * span
            const jz =
              iz * cell + reach + hash01i(seed, ix, key, k * 7 + 6) * span
            const jl = Math.hypot(jx, jy, jz)
            // Only cubes the sphere passes through hold craters.
            if (jl === 0 || Math.abs(jl - 1) > cell * 0.5) continue
            const sx = jx / jl
            const sy = jy / jl
            const sz = jz / jl
            const bias = 1 + asymmetry * (sx * fx + sy * fy + sz * fz)
            if (hash01i(seed, ix, key, k * 7 + 1) >= p * bias) continue
            const d = Math.hypot(x - sx, y - sy, z - sz)
            if (d >= reach) continue
            const age =
              hash01i(seed, ix, key, k * 7 + 5) * Math.max(0, Math.min(1, wear))
            acc += craterProfile(d, radius, depthRatio, age, complexRadius)
          }
    }
    return acc
  }
}

export interface PadOptions {
  /** Pad centre (world coords). */
  x: number
  z: number
  /** Radius of the DEAD-FLAT interior (m). */
  radius: number
  /** Pad surface height (m, absolute). */
  level: number
  /** Width of the blended skirt outside the flat interior (m) — the cut/fill
   * slope tying the pad into the terrain. Default `radius * 0.5`. */
  blend?: number
}

/**
 * A construction pad: dead-flat at `level` across the interior, a smooth
 * cut-and-fill skirt tying into the noise terrain beyond — how cities and
 * bases claim ground. No province (pads don't glow); compose several with
 * `composeLandforms` to terrace a settlement up a hillside.
 */
export function pad(opts: PadOptions): Bounded<LandformFn> {
  const { x: cx, z: cz, radius, level, blend = radius * 0.5 } = opts
  const outer = radius + Math.max(blend, 0.01)
  return withExtent(
    (x: number, z: number, h: number) => {
      const dx = x - cx
      const dz = z - cz
      const d = Math.sqrt(dx * dx + dz * dz)
      if (d >= outer) return h
      if (d <= radius) return level
      return h + (level - h) * smooth((outer - d) / (outer - radius))
    },
    // The skirt, not the flat interior — a pad claims ground out to `outer`.
    circleExtent(cx, cz, outer)
  )
}

export interface GulleyOptions {
  /** The FACE: where the cliff stands and the tunnel mouth sits. */
  x: number
  z: number
  /** Direction the gulley runs OUT from the face, in DEGREES (0 = +x,
   * 90 = +z). The tunnel drives the opposite way, into the hill. */
  heading: number
  /** Floor width (m) — forced flat across this. */
  width: number
  /** How far the gulley runs out from the face. */
  length: number
  /** Floor height at the face (m). ABSOLUTE, not a cut: the floor is this
   * height whether the natural ground was a hill or a hollow. */
  floorY: number
  /** Height of the forced cliff behind the face (m above the floor). This is
   * what the tunnel mouth is cut into, so it's the number that decides
   * whether the mouth has room. */
  cliffHeight?: number
  /** Distance over which the cliff rises, going INTO the hill (m). Small =
   * near-vertical. */
  faceRun?: number
  /** Floor grade going outward (m per m) — 0 is flat, positive climbs out. */
  grade?: number
  /** Fraction of `length` spent fading back to natural terrain (0..1). */
  fade?: number
  /** Lateral distance over which the forced floor fades to natural (m). */
  wallFade?: number
  /** Distance PAST the cliff top over which forcing fades back to the natural
   * surface (m). Too small and the crest is a step you fall off. */
  crestFade?: number
}

/**
 * A GULLEY: a **height-field FORCING FUNCTION** ending in a predictable cliff
 * — the "friendly geometry to mate with" that makes a tunnel mouth tractable.
 *
 * The distinction that matters, and that the first version got wrong: this
 * FORCES the height rather than cutting it. `min(h, floor)` only lowers ground
 * that was already high, so on a hillside you get a channel and in a hollow
 * you get nothing — the entrance is at the mercy of whatever terrain the seed
 * produced, which is precisely the unpredictability we were trying to escape.
 * Here the floor is `floorY` and the cliff is `cliffHeight` **whatever the
 * natural ground was doing**, so a tunnel author knows the geometry their
 * mouth will meet before they place it.
 *
 * The shape, along the axis:
 *
 * ```
 *      into the hill  |  the face  |        the gulley floor        | ambient
 *   ─────────────────╮|            |                                |
 *    natural terrain  ╲  cliffHeight                                 ╱
 *                      ╲___________|________________________________╱
 *                       ↑ faceRun    floorY, grading out over length
 * ```
 *
 * Forcing fades to the natural surface at the sides (`wallFade`) and at the
 * outer end (`fade`), so the channel joins the landscape instead of ending in
 * a second cliff. Everything outside is untouched, exactly.
 */
export function gulley(opts: GulleyOptions): Bounded<LandformFn> {
  const { x: fx, z: fz, heading: headingDeg, width, length, floorY } = opts
  const heading = headingDeg * (Math.PI / 180)
  const cliffHeight = opts.cliffHeight ?? 45
  const faceRun = Math.max(1e-3, opts.faceRun ?? 18)
  const grade = opts.grade ?? 0
  const fade = Math.min(0.9, Math.max(0.05, opts.fade ?? 0.35))
  const wallFade = Math.max(1e-3, opts.wallFade ?? width * 0.6)
  const crestFade = Math.max(1e-3, opts.crestFade ?? faceRun * 3)
  const cos = Math.cos(heading)
  const sin = Math.sin(heading)
  const halfW = width / 2
  const fadeStart = length * (1 - fade)
  /*
  The corridor's own two bail-outs ARE its footprint, read straight off the
  guards below: `along` outside [-faceRun - crestFade, length], or `lateral`
  past `halfW + wallFade`. Taking the numbers from the same expressions the
  code tests against is the point — an extent maintained separately from the
  guard it describes is an extent that goes stale the first time someone tunes
  a fade.
  */
  return withExtent((x: number, z: number, h: number) => {
    const dx = x - fx
    const dz = z - fz
    const along = dx * cos + dz * sin // + = outward from the face
    const lateral = Math.abs(-dx * sin + dz * cos)
    // Authority fades INTO the hill as well, over `crestFade` beyond the top
    // of the face. Without it the forced cliff-top met natural ground at full
    // authority and simply snapped — a 30m step at the crest, which is a
    // cliff you fall off rather than a rim you fly over.
    if (along < -faceRun - crestFade || along > length) return h
    if (lateral > halfW + wallFade) return h

    // The FORCED profile — a function of position only, never of `h`.
    const forced =
      along >= 0
        ? floorY + grade * along
        : floorY + cliffHeight * smooth(Math.min(1, -along / faceRun))

    // Authority: full across the floor and along the run, fading laterally to
    // the natural surface and easing out at the far end.
    const lateralW =
      lateral <= halfW ? 1 : 1 - smooth((lateral - halfW) / wallFade)
    const alongW =
      along < -faceRun
        ? 1 - smooth((-along - faceRun) / crestFade) // over the crest, into the hill
        : along <= fadeStart
        ? 1
        : 1 - smooth((along - fadeStart) / (length - fadeStart))
    const w = lateralW * alongW
    return h + (forced - h) * w
  }, corridorExtent(fx, fz, headingDeg, -faceRun - crestFade, length, halfW + wallFade))
}

export interface CoverOptions {
  /** Start of the corridor (usually the gulley's face). */
  x: number
  z: number
  /** Direction the corridor runs, in DEGREES (0 = +x, 90 = +z) — the way the
   * tunnel drives. Was radians until 0.7.0; see the CHANGELOG. */
  heading: number
  /** Corridor width (m). */
  width: number
  /** How far along the heading the cover extends (m). */
  length: number
  /** The ground is forced to AT LEAST this height over the corridor (m). */
  minHeight: number
  /** Lateral distance over which the forcing fades out (m). */
  fade?: number
}

/**
 * COVER — force the ground ABOVE a tunnel to be high enough that the tunnel
 * stays buried.
 *
 * This is the other half of making an entrance predictable, and the half the
 * first gulley missed: shaping the ground in FRONT of the face does nothing
 * about the ground OVER the run. Where the natural terrain dips below the
 * tunnel's roof, the tube surfaces — a glancing blow that opens a hole
 * nobody authored, complete with its own seam and its own tile skirts hanging
 * into the tunnel. That is exactly the pathology the gulley was adopted to
 * escape, reappearing 200m further in.
 *
 * `minHeight` should be the tunnel's ceiling plus a few metres of rock, so
 * the tube meets the surface EXACTLY ONCE — at the face, where the geometry
 * is conditioned for it. Raising ground is visually safe: it reads as the
 * hill the tunnel goes through.
 */
export function cover(opts: CoverOptions): Bounded<LandformFn> {
  const { x: sx, z: sz, heading: headingDeg, width, length, minHeight } = opts
  const heading = headingDeg * (Math.PI / 180)
  const fade = Math.max(1e-3, opts.fade ?? width * 0.7)
  const cos = Math.cos(heading)
  const sin = Math.sin(heading)
  const halfW = width / 2
  return withExtent(
    (x: number, z: number, h: number) => {
      if (h >= minHeight) return h // already deep enough: nothing to do
      const dx = x - sx
      const dz = z - sz
      const along = dx * cos + dz * sin
      if (along < 0 || along > length) return h
      const lateral = Math.abs(-dx * sin + dz * cos)
      if (lateral > halfW + fade) return h
      // Ease at both ends of the corridor as well as the sides, so the raised
      // ground is a ridge the tunnel runs under — not a wall across the map.
      const lateralW =
        lateral <= halfW ? 1 : 1 - smooth((lateral - halfW) / fade)
      const endW = 1 - smooth((along - length * 0.75) / (length * 0.25))
      const w = lateralW * Math.max(0, Math.min(1, endW))
      return h + (minHeight - h) * w
    },
    // `along` runs 0..length from the mouth; `lateral` reaches halfW + fade.
    corridorExtent(sx, sz, headingDeg, 0, length, halfW + fade)
  )
}

/** Chain landforms left → right (each sees the previous result). */
export function composeLandforms(...fns: LandformFn[]): LandformFn {
  const composed: LandformFn = (x, z, h) => {
    let acc = h
    for (const f of fns) acc = f(x, z, acc)
    return acc
  }
  return carryExtent(composed, fns)
}

/**
 * The union of the inputs' extents — or NO extent if any input lacks one.
 *
 * The asymmetry is the whole point and it is not conservatism for its own sake:
 * one unbounded member makes the composition unbounded, because a box drawn
 * around the members that *did* declare one would be a promise nobody made.
 * Returning nothing is honest and merely forfeits the optimisation; returning
 * the partial union would clip whatever the unannotated field was doing
 * outside it, invisibly.
 */
function carryExtent<F extends (...args: never[]) => number>(
  fn: F,
  parts: ReadonlyArray<unknown>
): F {
  let acc: Extent | null = null
  for (const p of parts) {
    const e = extentOf(p)
    if (e == null) return fn
    acc = acc == null ? e : unionExtent(acc, e)
  }
  return acc == null ? fn : withExtent(fn, acc)
}

/** Merge province fields by max — overlapping glows don't sum past 1. */
export function mergeProvinces(...fields: ProvinceFn[]): ProvinceFn {
  const merged: ProvinceFn = (x, z) => {
    let m = 0
    for (const f of fields) {
      const v = f(x, z)
      if (v > m) m = v
    }
    return m
  }
  return carryExtent(merged, fields)
}
